package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.expandingIcon
import com.siriusxm.pia.rest.unifiedaggregator.CloudEventChange
import com.siriusxm.pia.rest.unifiedaggregator.CloudEventChanges
import com.siriusxm.pia.rest.unifiedaggregator.DiffRowSideBySide
import com.siriusxm.pia.rest.unifiedaggregator.diffJson
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.math.max
import kotlin.math.min

private const val ORDER_PREVIEW_LIMIT = 25

private data class PrimitiveArrayDiffEntry(
    val value: String,
    val removedCount: Int,
    val addedCount: Int,
    val unchangedCount: Int
)

/**
 * Renders CloudEvent changes with a specialized view for primitive arrays.
 *
 * This component intentionally keeps the specialization at the CloudEvent UI
 * boundary. Shared diffing still comes from `CloudEventDiffView` and `diffJson`,
 * so primitive array order remains visible to other comparison workflows.
 *
 * @param changes The top-level CloudEvent field changes to render.
 */
@Composable
fun EnhancedCloudEventChangesView(changes: CloudEventChanges) {
    Style(EntityDiffStyles)

    Div({ classes(EntityDiffStyles.cloudEventDiffList) }) {
        for (change in changes.changes) {
            val oldValue = change.oldValue
            val newValue = change.value
            if (oldValue is JsonArray && newValue is JsonArray && oldValue.isPrimitiveArray() && newValue.isPrimitiveArray()) {
                PrimitiveArrayDiffView(change.key, oldValue, newValue)
            } else {
                CloudEventFieldDiffView(change)
            }
        }
    }
}

/**
 * Renders a standard CloudEvent field diff inside the shared diff card.
 *
 * @param change The top-level CloudEvent field change to render.
 */
@Composable
private fun CloudEventFieldDiffView(change: CloudEventChange) {
    val diffs = diffJson(change.key, change.oldValue, change.value)

    Div({ classes(EntityDiffStyles.cloudEventDiffContainer) }) {
        Div({ classes(EntityDiffStyles.primitiveArrayDiffTitle) }) {
            Text(change.key)
        }

        if (diffs.isEmpty()) {
            Div({ classes(EntityDiffStyles.diffEmpty) }) {
                Text("No changes in this update.")
            }
        } else {
            for (diff in diffs) {
                DiffRowSideBySide(diff.path, diff.oldValue, diff.newValue)
            }
        }
    }
}

/**
 * Displays a primitive array change as membership badges plus an ordered preview.
 *
 * Added, removed, and unchanged counts are computed as a multiset so duplicate
 * primitive values are represented correctly. If the arrays contain the same
 * values but in a different order, the component shows an explicit order-change
 * badge and expands the ordered preview by default.
 *
 * @param path The top-level CloudEvent field path being rendered.
 * @param oldArray The previous array value.
 * @param newArray The current array value.
 */
@Composable
internal fun PrimitiveArrayDiffView(path: String, oldArray: JsonArray, newArray: JsonArray) {
    val oldValues = oldArray.map { it.primitiveDisplayValue() }
    val newValues = newArray.map { it.primitiveDisplayValue() }
    val oldCounts = oldValues.groupingBy { it }.eachCount()
    val newCounts = newValues.groupingBy { it }.eachCount()
    val allValues = (oldCounts.keys + newCounts.keys).distinct().sorted()
    val entries = allValues.map { value ->
        val oldCount = oldCounts[value] ?: 0
        val newCount = newCounts[value] ?: 0
        PrimitiveArrayDiffEntry(
            value = value,
            removedCount = max(0, oldCount - newCount),
            addedCount = max(0, newCount - oldCount),
            unchangedCount = min(oldCount, newCount)
        )
    }

    val removed = entries.filter { it.removedCount > 0 }
    val added = entries.filter { it.addedCount > 0 }
    val unchanged = entries.filter { it.unchangedCount > 0 }
    val sameMembership = oldCounts == newCounts
    val orderChanged = oldValues != newValues

    Div({ classes(EntityDiffStyles.cloudEventDiffContainer) }) {
        Div({ classes(EntityDiffStyles.primitiveArrayDiffTitle) }) {
            Text(path)
        }

        Div({ classes(EntityDiffStyles.primitiveArrayDiffSummary) }) {
            Badge("${oldValues.size} old", BadgeColor.GRAY)
            Badge("${newValues.size} new", BadgeColor.GRAY)
            if (removed.isNotEmpty()) {
                Badge("${removed.sumOf { it.removedCount }} removed", BadgeColor.RED)
            }
            if (added.isNotEmpty()) {
                Badge("${added.sumOf { it.addedCount }} added", BadgeColor.GREEN)
            }
            if (unchanged.isNotEmpty()) {
                Badge("${unchanged.sumOf { it.unchangedCount }} unchanged", BadgeColor.GRAY)
            }
            if (orderChanged && sameMembership) {
                Badge("order changed", BadgeColor.ORANGE)
            }
        }

        PrimitiveArrayBadgeSection("Removed", removed, BadgeColor.RED) { it.removedCount }
        PrimitiveArrayBadgeSection("Added", added, BadgeColor.GREEN) { it.addedCount }
        PrimitiveArrayUnchangedSection(unchanged)

        if (orderChanged) {
            PrimitiveArrayOrderPreview(oldValues, newValues, expandedByDefault = sameMembership)
        }
    }
}

/**
 * Renders one non-empty added or removed primitive array section.
 *
 * @param title The section label displayed above the badges.
 * @param entries The values to render.
 * @param color The badge color for the section.
 * @param count The count selector for duplicate values.
 */
@Composable
private fun PrimitiveArrayBadgeSection(
    title: String,
    entries: List<PrimitiveArrayDiffEntry>,
    color: BadgeColor,
    count: (PrimitiveArrayDiffEntry) -> Int
) {
    if (entries.isEmpty()) return

    Div({ classes(EntityDiffStyles.primitiveArrayDiffSectionTitle) }) {
        Text(title)
    }
    PrimitiveArrayBadgeGroup(entries, color, count)
}

/**
 * Renders the unchanged primitive values behind an expand/collapse toggle.
 *
 * @param entries The unchanged values, including duplicate counts.
 */
@Composable
private fun PrimitiveArrayUnchangedSection(entries: List<PrimitiveArrayDiffEntry>) {
    if (entries.isEmpty()) return

    var expanded by remember { mutableStateOf(false) }
    val total = entries.sumOf { it.unchangedCount }

    Div({
        classes(EntityDiffStyles.primitiveArrayDiffToggle)
        onClick { expanded = !expanded }
    }) {
        expandingIcon(expanded) {
            onToggle { expanded = !expanded }
        }
        Text("$total unchanged values")
    }

    if (expanded) {
        PrimitiveArrayBadgeGroup(entries, BadgeColor.GRAY) { it.unchangedCount }
    }
}

/**
 * Renders primitive array values as wrapping badges, adding a count suffix for duplicates.
 *
 * @param entries The diff entries to display.
 * @param color The badge color for all entries.
 * @param count The count selector for duplicate values.
 */
@Composable
private fun PrimitiveArrayBadgeGroup(
    entries: List<PrimitiveArrayDiffEntry>,
    color: BadgeColor,
    count: (PrimitiveArrayDiffEntry) -> Int
) {
    Div({ classes(EntityDiffStyles.primitiveArrayDiffBadgeGroup) }) {
        for (entry in entries) {
            val entryCount = count(entry)
            val label = if (entryCount > 1) "${entry.value} x$entryCount" else entry.value
            Badge(label, color)
        }
    }
}

/**
 * Shows the changed array positions so order-only changes remain inspectable.
 *
 * The preview is capped to the first changed positions to keep very long arrays
 * usable, with a remaining-count line when more positions differ.
 *
 * @param oldValues The previous primitive values in original order.
 * @param newValues The current primitive values in original order.
 * @param expandedByDefault Whether to show the preview initially.
 */
@Composable
private fun PrimitiveArrayOrderPreview(
    oldValues: List<String>,
    newValues: List<String>,
    expandedByDefault: Boolean
) {
    val changedIndexes = (0 until max(oldValues.size, newValues.size))
        .filter { index -> oldValues.getOrNull(index) != newValues.getOrNull(index) }
    var expanded by remember { mutableStateOf(expandedByDefault) }

    Div({
        classes(EntityDiffStyles.primitiveArrayDiffToggle)
        onClick { expanded = !expanded }
    }) {
        expandingIcon(expanded) {
            onToggle { expanded = !expanded }
        }
        Text("${changedIndexes.size} ordered positions changed")
    }

    if (expanded) {
        Div({ classes(EntityDiffStyles.primitiveArrayOrderGrid) }) {
            PrimitiveArrayOrderCell("Index", isIndex = true)
            PrimitiveArrayOrderCell("Old")
            PrimitiveArrayOrderCell("New")

            for (index in changedIndexes.take(ORDER_PREVIEW_LIMIT)) {
                PrimitiveArrayOrderCell(index.toString(), isIndex = true)
                PrimitiveArrayOrderCell(oldValues.getOrNull(index) ?: "(not set)")
                PrimitiveArrayOrderCell(newValues.getOrNull(index) ?: "(not set)")
            }
        }

        val hiddenCount = changedIndexes.size - ORDER_PREVIEW_LIMIT
        if (hiddenCount > 0) {
            Div({ classes(EntityDiffStyles.diffNullValue) }) {
                Text("$hiddenCount more changed positions not shown.")
            }
        }
    }
}

/**
 * Renders one cell in the ordered primitive array preview grid.
 *
 * @param value The cell text.
 * @param isIndex Whether this cell should use index styling.
 */
@Composable
private fun PrimitiveArrayOrderCell(value: String, isIndex: Boolean = false) {
    Div({
        classes(
            if (isIndex) {
                EntityDiffStyles.primitiveArrayOrderIndex
            } else {
                EntityDiffStyles.primitiveArrayOrderValue
            }
        )
    }) {
        Text(value)
    }
}

private fun JsonArray.isPrimitiveArray(): Boolean = all { it is JsonPrimitive }

private fun JsonElement.primitiveDisplayValue(): String {
    val primitive = jsonPrimitive
    return primitive.contentOrNull ?: primitive.toString()
}
